import React, { useEffect, useRef, useContext } from "react";
import { Card, CardContent } from "@mui/material";
import PrescriberNotesHorizontalDisplay from "./PrescriberNotesHorizontalDisplay";
import PrescriberNoteVerticalDisplay from "./PrescriberNoteVerticalDisplay";
import DialogBox from "../CommonComponent/DialogBox";
import DynamicHtml from "../CommonComponent/DynamicHtml";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { SearchContext } from "../../../contexts/Search";
import { DynamicDataService } from "@services";

const PrescriberNotesDialog = ({
  isOpen,
  onClose,
  isMobile,
  prescriberNoteResponse,
  additionalPrescriberNoteCriteria,
  resultname,
  channelName,
  contingentDataField,
  row,
  onOpen = () => { },
}) => {
  const { appliedConfiguration } = useContext(ConfigurationContext);
  const { benefitType } = useContext(SearchContext);
  const benefitTypeId = benefitType == "Pharmacy" ? 2 : 1;
  const CUSTOM_VALUE_DATAFIELD = "_dataFieldsWithCustomValue";
  const headerFooter = appliedConfiguration.coverageFinderPrescriberNoteHeaderFooters.filter(
    (obj) => obj.benefitTypeId === benefitTypeId
  )[0];
  const sections = appliedConfiguration.coverageFinderPrescriberNoteSections.filter(
    (obj) => obj.benefitTypeId === benefitTypeId
  );
  const coverageFinderPrescriberNotesFields = appliedConfiguration.coverageFinderPrescriberNotes.filter(
    (obj) => obj.benefitTypeId === benefitTypeId && obj.prescriberNoteSectionId > 0
  );
  const coverageFinderPrescriberNotesFieldsWithoutSection = appliedConfiguration.coverageFinderPrescriberNotes.filter(
    (obj) =>
      obj.benefitTypeId === benefitTypeId && (obj.prescriberNoteSectionId == null || obj.prescriberNoteSectionId === 0)
  );
  const coverageFinderPrescriberNotesFieldsWithExternalFile = appliedConfiguration.coverageFinderPrescriberNotes.filter(
    (obj) => obj.benefitTypeId === benefitTypeId && (obj.externalFile != null || obj.externalFile != "")
  );

  const prescriberNoteLogic = appliedConfiguration.coverageFinderPrescriberNoteLogics.filter(
    (obj) => obj.benefitTypeId === benefitTypeId
  )[0];

  const prescriberNoteResponseLowerCase = prescriberNoteResponse?.map((item) =>
    Object.keys(item).reduce((acc, key) => {
      acc[key.toLowerCase()] = item[key];
      return acc;
    }, {})
  )[0];
  const scrollableContent = useRef(null);

  const hideSectionIfEmpty =
    prescriberNoteLogic?.hideSectionFieldIfNoData?.toLowerCase() === "yes" &&
    prescriberNoteLogic?.emptySectionOrFieldValue?.toLowerCase() === "hide the section completely";

  const showSection = (section) => {
    if (!hideSectionIfEmpty) {
      return true;
    }

    const noteFields = coverageFinderPrescriberNotesFields.filter((obj) => obj.prescriberNoteSectionId === section.id);
    const fieldDataExists = prescriberNoteResponseLowerCase != null && noteFields.some(
      (field) =>
        prescriberNoteResponseLowerCase[field.dataField.toLowerCase()] !== "" &&
        prescriberNoteResponseLowerCase[field.dataField.toLowerCase()] !== null &&
        prescriberNoteResponseLowerCase[field.dataField.toLowerCase()] !== undefined
    );

    const resultRowDataExists = noteFields.some(
      (field) =>
        row &&
        row[field.dataField] !== "" &&
        row[field.dataField] !== null &&
        row[field.dataField] !== undefined
    );

    let coverageContingentDataExists = false;
    const contingentNoteFields = noteFields.filter((field) => field.coverageContingentData);
    if (contingentNoteFields && contingentNoteFields.length > 0 && row && contingentDataField) {
      const isCustomValue =
        row[CUSTOM_VALUE_DATAFIELD]?.split(",")?.findIndex((item) => item === contingentDataField.toLowerCase()) > -1;
      const { rowValue } = DynamicDataService.extractDynamicRowData(row[contingentDataField]);
      coverageContingentDataExists = !isCustomValue && rowValue !== "" && rowValue !== null && rowValue !== undefined;
    }

    let statusFieldExists = false;
    const statusNoteFields = noteFields.filter((field) => field.status);
    if (statusNoteFields && statusNoteFields.length > 0) {
      const status = row["Status"] ?? row["majority_Status"] ?? "";
      statusFieldExists = !!status;
    }

    return fieldDataExists || coverageContingentDataExists || statusFieldExists || resultRowDataExists;
  };

  useEffect(() => {
    if (isOpen) {
      // Reset scroll position to top when dialog box opens
      if (scrollableContent.current) {
        scrollableContent.current.scrollTop = 0;
      }
      onOpen();
      document.body.classList.add("no-scroll");
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <DialogBox isOpen={isOpen} onClose={onClose}>
          <div
            ref={scrollableContent}
            id="scrollable-note-content"
            part="cfScrollableContent"
            className="norstella-cf-scrollable-content"
          >
            <div className="norstella-cf-note-content" part="cfNoteContent">
              <Card className="norstella-cf-prescriberNotesCard" part="cfPrescriberNotesCard">
                <div className="norstella-cf-divPrescriberNotesCard" part="cfDivPrescriberNotesCard">
                  {headerFooter && headerFooter.header && headerFooter.header.trim() !== "" && (
                    <DynamicHtml
                      input={headerFooter.header}
                      resultname={resultname}
                      className="norstella-cf-prescriberNoteHeader"
                      part="cfPrescriberNoteHeader"
                      channelName={channelName || ""}
                    />
                  )}
                  {sections.length == 0 &&
                    coverageFinderPrescriberNotesFieldsWithoutSection.length == 0 &&
                    coverageFinderPrescriberNotesFieldsWithExternalFile.length == 0 && (
                      <Card
                        className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteNoDetails"
                        part="cfNoteContent"
                      >
                        <div className="norstella-cf-notedetails">There are no criteria details available.</div>
                      </Card>
                    )}
                  {!isMobile && sections != null && (
                    <>
                      <CardContent
                        part="cfPrescriberNotesCardContentHorizontal"
                        className="norstella-cf-prescriberNotesCardContentHorizontal"
                      >
                        {sections.map(
                          (section, i) =>
                            showSection(section) && (
                              <Card
                                className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardHorizontal"
                                part="cfNoteContent"
                                key={i}
                              >
                                <DynamicHtml
                                  input={section.sectionName}
                                  className="norstella-cf-prescriberNoteSection"
                                  part="cfPrescriberNoteSection"
                                ></DynamicHtml>
                                {coverageFinderPrescriberNotesFields != null &&
                                  section.orientationId == 1 && (
                                    <PrescriberNotesHorizontalDisplay
                                      section={section}
                                      prescriberNoteResponse={prescriberNoteResponse}
                                      coverageFinderPrescriberNotesFields={coverageFinderPrescriberNotesFields}
                                      contingentDataField={contingentDataField}
                                      row={row}
                                    />
                                  )}

                                {coverageFinderPrescriberNotesFields != null &&
                                  section.orientationId == 2 && (
                                    <PrescriberNoteVerticalDisplay
                                      section={section}
                                      prescriberNoteResponse={prescriberNoteResponse}
                                      coverageFinderPrescriberNotesFields={coverageFinderPrescriberNotesFields}
                                      contingentDataField={contingentDataField}
                                      row={row}
                                    />
                                  )}
                                {prescriberNoteResponse == null ||
                                  (coverageFinderPrescriberNotesFields == null && (
                                    <Card
                                      className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardHorizontalNoDetails"
                                      part="cfNoteContent"
                                    >
                                      <div className="norstella-cf-notedetails">
                                        There are no criteria details available.
                                      </div>
                                    </Card>
                                  ))}
                              </Card>
                            )
                        )}
                      </CardContent>
                    </>
                  )}
                  {isMobile && sections != null && (
                    <>
                      <CardContent
                        part="cfPrescriberNotesCardContentVertical"
                        className="norstella-cf-prescriberNotesCardContentVertical"
                      >
                        {sections.map(
                          (section, i) =>
                            showSection(section) && (
                              <Card
                                className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardVertical"
                                part="cfNoteContent"
                                key={i}
                              >
                                <DynamicHtml
                                  input={section.sectionName}
                                  className="norstella-cf-prescriberNoteSection"
                                  part="cfPrescriberNoteSection"
                                ></DynamicHtml>
                                {coverageFinderPrescriberNotesFields != null &&
                                  section.mobileOrientationId == 1 && (
                                    <PrescriberNotesHorizontalDisplay
                                      section={section}
                                      prescriberNoteResponse={prescriberNoteResponse}
                                      coverageFinderPrescriberNotesFields={coverageFinderPrescriberNotesFields}
                                      contingentDataField={contingentDataField}
                                      row={row}
                                    />
                                  )}
                                {coverageFinderPrescriberNotesFields != null &&
                                  section.mobileOrientationId == 2 && (
                                    <PrescriberNoteVerticalDisplay
                                      section={section}
                                      prescriberNoteResponse={prescriberNoteResponse}
                                      coverageFinderPrescriberNotesFields={coverageFinderPrescriberNotesFields}
                                      contingentDataField={contingentDataField}
                                      row={row}
                                    />
                                  )}
                                {prescriberNoteResponse == null ||
                                  (coverageFinderPrescriberNotesFields == null && (
                                    <Card
                                      className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardVerticalNoDetails"
                                      part="cfNoteContent"
                                    >
                                      <div className="norstella-cf-notedetails">
                                        There are no criteria details available.
                                      </div>
                                    </Card>
                                  ))}
                              </Card>
                            )
                        )}
                      </CardContent>
                    </>
                  )}
                  {coverageFinderPrescriberNotesFieldsWithoutSection.length > 0 && (
                    <>
                      <CardContent
                        part="cfNoteWithoutSectionContent"
                        className="norstella-cf-noteWithoutSectionContent"
                      >
                        <div>
                          {coverageFinderPrescriberNotesFieldsWithoutSection != null && (
                            <Card
                              className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardWithoutSection"
                              part="cfNoteContent"
                            >
                              <PrescriberNoteVerticalDisplay
                                prescriberNoteResponse={prescriberNoteResponse}
                                coverageFinderPrescriberNotesFields={
                                  coverageFinderPrescriberNotesFieldsWithoutSection
                                }
                                contingentDataField={contingentDataField}
                                row={row}
                              />
                            </Card>
                          )}
                          {prescriberNoteResponse == null ||
                            (coverageFinderPrescriberNotesFields == null && (
                              <Card
                                className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardWithoutSectionNoDetails"
                                part="cfNoteContent"
                              >
                                <div className="norstella-cf-notedetails">There are no criteria details available.</div>
                              </Card>
                            ))}
                        </div>
                      </CardContent>
                    </>
                  )}
                  {additionalPrescriberNoteCriteria &&
                    additionalPrescriberNoteCriteria.length > 0 &&
                    coverageFinderPrescriberNotesFieldsWithExternalFile &&
                    coverageFinderPrescriberNotesFieldsWithExternalFile.length > 0 && (
                      <>
                        <CardContent
                          part="cfPrescriberNotesCardContentAdditional"
                          className="norstella-cf-prescriberNotesCardContentAdditional"
                        >
                          {additionalPrescriberNoteCriteria.map((additional, i) => (
                            <>
                              {additional != null && (
                                <div part="cfAdditionalNoteDiv" className="norstella-cf-additionalNoteDiv" key={i}>
                                  <Card
                                    className="norstella-cf-prescriberNoteCardContent norstella-cf-prescriberNoteCardAdditionalData"
                                    part="cfNoteContent"
                                  >
                                    <PrescriberNoteVerticalDisplay
                                      prescriberNoteResponse={additional}
                                      coverageFinderPrescriberNotesFields={
                                        coverageFinderPrescriberNotesFieldsWithExternalFile
                                      }
                                      contingentDataField={contingentDataField}
                                      row={row}
                                    />
                                  </Card>
                                </div>
                              )}
                            </>
                          ))}
                        </CardContent>
                        <div
                          className="norstella-cf-divPrescriberNotesAdditionalPadding"
                          style={{ padding: "10px" }}
                        ></div>
                      </>
                    )}
                  {headerFooter && headerFooter.footer && headerFooter.footer.trim() !== "" && (
                    <DynamicHtml
                      input={headerFooter.footer}
                      resultname={resultname}
                      className="norstella-cf-prescriberNoteFooter"
                      part="cfPrescriberNoteFooter"
                      channelName={channelName || ""}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </DialogBox>
      )}
    </>
  );
};

export default PrescriberNotesDialog;
